'use client';
import { addEventParam, INinjaEvent, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import { IAdaptiveActionCard, safeStringify } from '@ninjacart/nc-nk-core';
import { logEvent } from '@ninjacart/nc-nk-firebase';
import {
  checkLocationPermission,
  handleMarketingEvents,
  IWeatherApiClient,
  KissanEventType,
  msToTime,
  updateUserLanguage,
  useAppPersistenceStore,
  useCropPreferenceStore,
  useEdTechProductStore,
  useEventZStore,
  useFeedStore,
  useProfileStore,
  useWeatherStore
} from '@ninjacart/nc-nk-store';
import { useTheme } from '@ninjacart/nc-nk-ui';
import { handleContactPermission, useFocusEffect } from '@ninjacart/nc-nk-web-deps';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Appearance, AppState, LayoutRectangle, Platform, StatusBar } from 'react-native';
import { useFeedApi } from './useFeedAPI';

const MOMENTUM_SCROLL_END_THROTTLE_MILLISECONDS = 300;

let Location: typeof import('expo-location') | undefined = undefined;
if (Platform.OS !== 'web') {
  Location = require('expo-location');
}

export function useHome({ initialData }: { initialData?: any }) {
  // const [isScrolling, setIsScrolling] = useState(false);
  const [layout, setLayout] = useState<LayoutRectangle>();

  const momentumScrollEndTimer = useRef<NodeJS.Timeout>();
  const [homeFeedsCard, setHomeFeedsCard] = useState<Array<IAdaptiveActionCard>>(
    (initialData?.data as Array<IAdaptiveActionCard>) || []
  );
  const initialRefreshState = initialData?.data?.length < 0;
  const [refreshing, setRefreshing] = useState(initialRefreshState);
  const [onEndReachedSpinner, setOnEndReachedSpinner] = useState(false);
  const userProfile = useProfileStore((state) => state.userProfile);
  const activeTheme = Appearance.getColorScheme();
  const userCurrentTheme = activeTheme === 'dark' ? 'Dark' : 'Light';
  const processEvents = useRef(useQueuedEventStore((state) => state.processEvents)).current;
  const headerHeight = 128;

  const { getHomeFeeds, loading } = useFeedApi();
  const HomeStartTimeRef = useRef(performance.now());
  const appStartingTime = useEventZStore((state) => state.appStartingTime);
  const uniqueDeviceId = useAppPersistenceStore((state) => state.deviceId);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const setHasRequestedLocationPerm = useProfileStore((state) => state.setHasRequestedLocationPerm);
  const [isWeatherWidgetAvailable, setIsWeatherWidgetAvailable] = useState(false);
  const latitude = useProfileStore((state) => state.latitude);
  const setLatitude = useProfileStore((state) => state.setLatitude);
  const setLongitude = useProfileStore((state) => state.setLongitude);
  const longitude = useProfileStore((state) => state.longitude);
  const setState = useProfileStore((state) => state.setState);
  const setCity = useProfileStore((state) => state.setCity);
  const setCountry = useProfileStore((state) => state.setCountry);
  const hasRequestedLocationPerm = useProfileStore((state) => state.hasRequestedLocationPerm);
  const [extraData, setExtraData] = useState(false);
  const setWeatherClientApiDetail = useWeatherStore((state) => state.setWeatherClientApiDetail);
  const flashListRef = useRef<any | null>(null);
  const setUserFirstOpenTime = useEventZStore((state) => state.setUserFirstOpenTime);
  const userFirstOpenTime = useEventZStore((state) => state.userFirstOpenTime);
  const isOneDayCompleted = 24 * 60 * 60 * 1000;
  const thirtyMinutesInMs = 30 * 60 * 1000; // 30 minutes in milliseconds
  const [isThirtyMinutesPassed, setIsThirtyMinutesPassed] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(performance.now());
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const appLanguage = useAppPersistenceStore.getState().appLanguage;
  const appState = useRef(AppState.currentState);
  const [appStateVisible, setAppStateVisible] = useState(appState.current);
  const setScreenName = useEdTechProductStore((state) => state.setScreenName);
  const contactIndex = useEventZStore((state) => state.contactIndex);
  const cropPreference = useCropPreferenceStore((state) => state.cropPreference);
  const animalPreference = useCropPreferenceStore((state) => state.animalPreference);
  const setShortVideos = useFeedStore((state) => state.setShortVideos);
  const theme = useTheme();
  const paginationCount = useRef({
    stream: Platform.OS === 'web' ? 3 : 10,
    shortVideo: Platform.OS === 'web' ? 5 : 10,
  });

  const shortVideoRef = useRef({ offset: 0, count: paginationCount.current.shortVideo, next: '' });
  
  const handleHomePageInitiateEvent = () => {
    const screenTiming = msToTime(HomeStartTimeRef.current - appStartingTime);
    const homepageInitiatedEvent: INinjaEvent = {
      name: KissanEventType.HOMEPAGE_INITIATED_SUCCESSFULLY,
      screenDetails: { screen: 'feed page', screenTimeInSecs: screenTiming },
      eventParams: [addEventParam('activeColorTheme', userCurrentTheme)],
    };
    processEvents([homepageInitiatedEvent]);
  };

  useEffect(function cleanup() {
    handleHomePageInitiateEvent();
    return () => {
      clearTimeout(momentumScrollEndTimer.current);
    };
  }, []);

  // Pagination refs

  const paginationRef = useRef({
    offset: initialData?.offset || 0,
    count: Platform.OS === 'web' ? 3 : 10,
    next: '',
    refresh: true,
  });

  // Handle app state changes
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (appState.current.match(/inactive|background/) && nextAppState === 'active') {
        getLatAndLong();
      }

      appState.current = nextAppState;
      setAppStateVisible(appState.current);
    });
    patchUserLanguage();
    setScreenName('feed page');
    if (contactIndex) {
      handleContactPermission();
    }

    return () => {
      subscription.remove();
    };
  }, []);

  // useEffect(() => {
  //   if (!hasMounted) {
  //     setHasMounted();
  //     return;
  //   }
  //   setRefreshing(true);
  // }, [appLanguage]);

  const patchUserLanguage = async () => {
    if (userProfile) {
      try {
        let needsPatch = false;

        if (!userProfile?.user?.preferences) needsPatch = true;
        if (userProfile?.user?.preferences?.languagePreference !== appLanguage) needsPatch = true;

        if (needsPatch) {
          console.debug('Needs patch for language update => ', needsPatch);
          await updateUserLanguage(appLanguage ?? 'hi');
        }
      } catch (error) {
        console.error('Error while patching user language => ', error);
      }
    }
  };

  // theming related effects
  useFocusEffect(
    useCallback(() => {
      StatusBar.setBarStyle(activeTheme === 'dark' ? 'light-content' : 'dark-content', true);
      StatusBar.setBackgroundColor(theme['homeStatusBarColor']?.val);

      return () => {
        StatusBar.setBackgroundColor('transparent');
        StatusBar.setBarStyle(activeTheme === 'dark' ? 'light-content' : 'dark-content', true);
      };
    }, [activeTheme, theme])
  );

  const onRefresh = async () => {
    console.log('On refresh called!!!');
    // a();
    setRefreshing(true);
    paginationRef.current.offset = 0;
    paginationRef.current.count = paginationCount.current.stream;
    paginationRef.current.next = '';
    paginationRef.current.refresh = true;
    shortVideoRef.current.offset = 0;
    shortVideoRef.current.count = paginationCount.current.shortVideo;
    shortVideoRef.current.next = '';
    if (Platform.OS !== 'web') {
      pullToRefreshEvents();
    }
  };

  const pullToRefreshEvents = () => {
    const additionalDetails = [
      addEventParam('offset', paginationRef.current?.offset),
      addEventParam('count', paginationRef.current?.count),
      addEventParam('length', flashListRef?.current?.state?.data?.length || 0),
      addEventParam('userId', userProfile?.user?.id ? userProfile?.user?.id : '-1'),
      addEventParam('deviceId', uniqueDeviceId),
    ];
    const homePagePullToRefreshEvent: INinjaEvent = {
      name: KissanEventType.HOMEPAGE_PULL_TO_REFRESH,
      screenDetails: { screen: 'feed page' },
      eventParams: additionalDetails,
    };
    enqueue(homePagePullToRefreshEvent);
  };

  const handleAnonymousUserEvent = () => {
    const anonymousUserEvent: INinjaEvent = {
      name: KissanEventType.ANONYMOUS_USER_NOT_LOGGEDIN,
      screenDetails: { screen: 'feed page' },
    };
    enqueue(anonymousUserEvent);
  };

  useEffect(() => {
    // if (hasUserLoggedIn()) {
    //   novuDeleteSubscriber(uniqueDeviceId);
    // }

    if (!hasUserLoggedIn() && !isThirtyMinutesPassed) {
      timerRef.current = setInterval(() => {
        const currentTime = performance.now();
        const elapsedTimeMs = currentTime - startTimeRef.current;

        if (elapsedTimeMs >= thirtyMinutesInMs) {
          setIsThirtyMinutesPassed(true);
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          handleAnonymousUserEvent();
        }
      }, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [hasUserLoggedIn, isThirtyMinutesPassed]);

  const onEndReached = async () => {
    console.log('On end reached!!!', paginationRef.current);
    if (loading) return;
    setOnEndReachedSpinner(true);
    if (Platform.OS !== 'web') {
      if (!hasRequestedLocationPerm) {
        console.log('Ask location perms!!!');
        await locationPermissionAsk();
      }
    }
    try {
      const newFeeds = await getHomeFeeds(paginationRef.current);
      if (!newFeeds?.data?.length) {
        return;
      }

      if (newFeeds) {
        setHomeFeedsCard((prev) => [...prev, ...newFeeds.data]);
        paginationRef.current = {
          ...paginationRef.current,
          offset: newFeeds.offset,
          next: newFeeds.next,
          refresh: false,
        };
        const hasWeatherInfo = newFeeds?.data.find((ele) => ele.type === 'WEATHER');
        setWeatherClientApiDetail(
          hasWeatherInfo?.actionData?.content as unknown as IWeatherApiClient
        );
      }
    } catch (error) {
      console.error('Error fetching more feeds:', error);
    } finally {
      setOnEndReachedSpinner(false);
    }
  };

  useEffect(() => {
    const currentTime = new Date().getTime();

    // If there's no stored entry time, save the current time
    if (!userFirstOpenTime) {
      setUserFirstOpenTime(String(currentTime));
    } else {
      const timeDiff = currentTime - Number(userFirstOpenTime);

      // Check if 24 hours have passed
      if (timeDiff >= isOneDayCompleted) {
        const userReturnEvent: INinjaEvent = {
          name: KissanEventType.RETURNING_USER,
          screenDetails: { screen: 'feed page' },
        };
        enqueue(userReturnEvent);
        logEvent('RETURNING_USER', {
          screen: 'feed page',
          userId: userProfile?.user?.id ? userProfile?.user?.id : '-1',
        });
        setUserFirstOpenTime(String(currentTime));
      }
    }
  }, [userFirstOpenTime]);

  const handleHomepageApiEvents = () => {
    handleMarketingEvents(KissanEventType.HOMEPAGE_LOAD_SUCCESS, { screen: 'feed page' }, [
      addEventParam('userId', userProfile?.user?.id ? userProfile?.user?.id : '-1'),
    ]);
    const homePageLoadSuccessEvent: INinjaEvent = {
      name: KissanEventType.HOMEPAGE_LOAD_SUCCESS,
      screenDetails: { screen: 'Feed page' },
      eventParams: [addEventParam('activeColorTheme', userCurrentTheme)],
    };

    processEvents([homePageLoadSuccessEvent]);
  };

  useEffect(() => {
    if (initialData?.data?.length) {
      handleHomepageApiEvents();
    }
  }, [initialData?.data]);

  const handleHomepageAPIError = (error) => {
    logEvent('HOMEPAGE_LOAD_FAILED', {
      screen: 'feed page',
      userId: userProfile?.user?.id ? userProfile?.user?.id : '-1',
    });

    const homePageLoadFailedEvent: INinjaEvent = {
      name: KissanEventType.HOMEPAGE_LOAD_FAILED,
      screenDetails: { screen: 'feed page' },
      eventParams: [
        addEventParam('errorMessage', error.toString()),
        addEventParam('apiError', safeStringify(error)),
      ],
    };
    processEvents([homePageLoadFailedEvent]);
  };

  // Handle the actual data fetching
  // this is not required since we fetch
  // from router for both native and msite
  // useEffect(() => {
  //   // console.log('Is refreshinhg:', refreshing);
  //   if (refreshing) {
  //     console.log('Fetching data!!! client');
  //     (async () => {
  //       try {
  //         const feeds = await getHomeFeeds(paginationRef.current);
  //         if (feeds) {
  //           setHomeFeedsCard(feeds.data);
  //           paginationRef.current = {
  //             ...paginationRef.current,
  //             offset: feeds.offset,
  //             next: feeds.next,
  //             refresh: false,
  //           };
  //           handleHomepageApiEvents();
  //           const hasWeatherInfo = feeds.data.find((ele) => ele.type === 'WEATHER');
  //           const shortFeedDataIndex = feeds.data.findIndex((ele) => ele.type == 'SHORTS');
  //           setWeatherClientApiDetail(
  //             hasWeatherInfo?.actionData?.content as unknown as IWeatherApiClient
  //           );
  //           if (shortFeedDataIndex > -1) {
  //             const data = feeds.data[shortFeedDataIndex] as unknown as IESShortVideoData<
  //               Array<IESSShortVideoActionData>
  //             >;

  //             setShortVideos(data.actionData);
  //             paginationRef.current.offset = feeds.offset;
  //             paginationRef.current.count = paginationCount.current.stream;
  //             paginationRef.current.next = feeds.next;
  //           }
  //           setRefreshing(false);
  //         }
  //       } catch (error) {
  //         console.error('Error fetching feeds:', error);
  //         handleHomepageAPIError(error);
  //         setRefreshing(false);
  //       }
  //     })();
  //   }
  // }, [refreshing, getHomeFeeds]);

  async function locationPermissionAsk() {
    const hasLocationPermission = await checkLocationPermission();
    setHasRequestedLocationPerm(true);
    if (!hasLocationPermission) {
      handleLocationPermissionDeniedEvent();
    } else {
      handleLocationPermissionGivenEvent();
      startWatching();
      setIsWeatherWidgetAvailable(true);
    }
  }

  const startWatching = async () => {
    let subscription;
    // const Location = await import('expo-location');
    try {
      subscription = await Location?.watchPositionAsync(
        {
          timeInterval: 10000, // Update location every 10 seconds (adjust as needed)
          distanceInterval: 10, // Update location if device moves more than 10 meters
        },
        (location) => {
          if (location && location.coords) {
            handleReverseGeocode(location);
          }
        }
      );
    } catch (error) {
      console.error('Error starting to watch location:', error);
    }
    return () => {
      if (subscription) {
        subscription.remove();
      }
    };
  };

  const handleReverseGeocode = async (newLocation) => {
    if (newLocation && newLocation.coords) {
      // const Location = await import('expo-location');
      const { latitude, longitude } = newLocation.coords;
      setLatitude(latitude);
      setLongitude(longitude);
      try {
        const geocodeResult = await Location?.reverseGeocodeAsync({ latitude, longitude });
        const { city, region, country } = geocodeResult?.[0] || {};
        setCity(city);
        setState(region);
        setCountry(country);
      } catch (error) {
        console.error('Error reverse geocoding:', error);
      }
    }
  };

  const handleLocationPermissionGivenEvent = () => {
    const locationPermissionGivenEvent: INinjaEvent = {
      name: KissanEventType.LOCATION_PERMISSION_GIVEN_BY_USER,
      screenDetails: { screen: 'location page' },
      eventParams: [addEventParam('userPermissionGiven', 'User given the location permission')],
    };
    enqueue(locationPermissionGivenEvent);
  };

  const handleLocationPermissionDeniedEvent = () => {
    const locationPermissionDeniedEvent: INinjaEvent = {
      name: KissanEventType.LOCATION_PERMISSION_DENIED_BY_USER,
      screenDetails: { screen: 'location page' },
      eventParams: [addEventParam('userPermissionGiven', 'user denied the location permission')],
    };
    enqueue(locationPermissionDeniedEvent);
  };

  async function weatherLocationPermissionAsk() {
    const hasLocationPermission = await checkLocationPermission();
    if (!hasLocationPermission) {
      handleLocationPermissionDeniedEvent();
      return true;
    } else {
      handleLocationPermissionGivenEvent();
      // Set weather widget available immediately when permission is granted
      setIsWeatherWidgetAvailable(true);
      getLatAndLong();
    }
  }

  // useEffect(() => {
  //   if (askLocationPermissionWeather) {
  //     weatherLocationPermissionAsk();
  //   }
  // }, [askLocationPermissionWeather]);

  const getLatAndLong = async () => {
    try {
      // const Location = await require('expo-location');
      const hasLocationPermission = await Location?.getForegroundPermissionsAsync();
      if (hasLocationPermission?.granted) {
        setIsWeatherWidgetAvailable(true);
        // Also try to get current position to update coordinates
        try {
          const currentPosition = await Location?.getCurrentPositionAsync({
            accuracy: Location.Accuracy.High,
          });
          if (currentPosition?.coords) {
            handleReverseGeocode(currentPosition);
          }
        } catch (positionError) {
          console.error('Error getting current position:', positionError);
        }
      }
    } catch (error) {
      console.error('Error getting location permissions:', error);
    }
  };

  useEffect(() => {
    if (isWeatherWidgetAvailable) {
      setExtraData(!extraData);
      return;
    }

    getLatAndLong();
  }, [isWeatherWidgetAvailable]);

  useEffect(() => {
    if (cropPreference?.length > 0 || animalPreference?.length > 0) {
      setExtraData(!extraData);
      return;
    }
  }, [cropPreference, animalPreference]);

  return {
    homeFeedsCard,
    loading,
    refreshing,
    onEndReachedSpinner,
    headerHeight,
    isWeatherWidgetAvailable,
    globalSearchEnabled: true, // Get from store
    // scrollY,
    // previousScrollY,
    // headerOffset,
    setRefreshing,
    onRefresh,
    onEndReached,
    // scrollHandler,
    // headerStyles,
    setHomeFeedsCard,
    setExtraData,
    extraData,
    locationPermissionAsk,
    weatherLocationPermissionAsk,
    flashListRef,
    cropPreference,
    animalPreference,
    layout,
    setLayout,
  };
}
